import { useEffect } from 'react';

// Global counter to track how many components are requesting scroll lock
let scrollLockCount = 0;
// Store the original scroll position when first locked
let originalScrollY = 0;

/**
 * Custom hook to lock/unlock body scrolling
 * Handles both desktop and iOS/mobile touch scrolling
 * Uses a reference counter to handle multiple modals properly
 * @param isLocked - Whether scrolling should be locked
 */
export const useScrollLock = (isLocked: boolean) => {
  useEffect(() => {
    if (isLocked) {
      // Increment the lock counter
      scrollLockCount++;

      // Only apply styles on the first lock
      if (scrollLockCount === 1) {
        // Capture the current scroll position
        originalScrollY = window.scrollY;

        // Apply scroll lock
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${originalScrollY}px`;
        document.body.style.width = '100%';
      }

      // Prevent touch move events on iOS (for the transparent overlay issue)
      const preventTouchMove = (e: TouchEvent) => {
        // Allow multi-touch gestures (pinch-to-zoom)
        if (e.touches.length > 1) {
          return;
        }

        // Allow scrolling within elements that have overflow
        const target = e.target as HTMLElement;
        const scrollableParent = target.closest('[data-scroll-lock-scrollable]');

        // Allow touch events on zoomable images
        const isZoomableImage = target.tagName === 'IMG' && target.hasAttribute('data-zoomable');

        if (!scrollableParent && !isZoomableImage) {
          e.preventDefault();
        }
      };

      // Add passive: false to ensure preventDefault works
      document.addEventListener('touchmove', preventTouchMove, { passive: false });

      // Cleanup: only unlock when all locks are released
      return () => {
        // Decrement the lock counter (with safety check)
        scrollLockCount = Math.max(0, scrollLockCount - 1);

        // Remove touch event listener for this instance
        document.removeEventListener('touchmove', preventTouchMove);

        // Only restore scrolling when the last lock is released
        if (scrollLockCount === 0) {
          // Remove all scroll lock styles
          document.body.style.overflow = '';
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.width = '';

          // Restore scroll position
          window.scrollTo(0, originalScrollY);
        }
      };
    }
  }, [isLocked]);
};
